// ============================================================
// File: src/components/UserForm.js
// Purpose: Add User Drawer Form (consistent with AddTenant.js)
// Updated: 2025-11-04 — Drawer-based final version
// ============================================================

import React, { useEffect, useState, useContext } from "react";
import Select from "react-select";
import { Spinner } from "react-bootstrap";
import {
  createUser,
  getActiveTenants,
  getLocationsByTenant,
  getDepartmentsByTenant,
  getRolesByTenant,
} from "../services/userService";
import { AuthContext } from "../context/AuthContext";
import "../styles/UserForm.css";

export default function UserForm({ onClose, onSuccess }) {
  const { user } = useContext(AuthContext);
  const roleCode = (user?.role_code || "").toUpperCase();
  const userTenantId = user?.tenant_id || null;

  const [tenants, setTenants] = useState([]);
  const [locations, setLocations] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [roles, setRoles] = useState([]);

  const [selectedTenant, setSelectedTenant] = useState(null);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [selectedDepartments, setSelectedDepartments] = useState([]);
  const [selectedRoles, setSelectedRoles] = useState([]);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    mobile: "",
    is_employee: false,
    status: "Active",
  });

  const [saving, setSaving] = useState(false);

  // ============================================================
  // 🎨 Gradient multi-select styles
  // ============================================================
  const selectStyles = {
    container: (base) => ({ ...base, minWidth: 220, zIndex: 10 }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    multiValue: (base) => ({
      ...base,
      background:
        "linear-gradient(90deg, rgba(0,123,255,0.9), rgba(111,66,193,0.9))",
      borderRadius: "12px",
      color: "white",
    }),
    multiValueLabel: (base) => ({
      ...base,
      color: "white",
      fontWeight: "500",
    }),
    multiValueRemove: (base) => ({
      ...base,
      color: "white",
      ":hover": { backgroundColor: "rgba(255,255,255,0.2)", color: "white" },
    }),
  };

  // ============================================================
  // 🔹 Load Tenants
  // ============================================================
  useEffect(() => {
    const loadTenants = async () => {
      try {
        if (roleCode === "SUPER_ADMIN") {
          const data = await getActiveTenants();
          const options = (data || []).map((t) => ({
            value: t.id,
            label: t.name,
          }));
          setTenants(options);
          const defaultTenant =
            options.find((t) => t.value === 1) || options[0] || null;
          if (defaultTenant) setSelectedTenant(defaultTenant);
        } else if (userTenantId) {
          const option = {
            value: userTenantId,
            label: user?.tenant_name || "Current Tenant",
          };
          setTenants([option]);
          setSelectedTenant(option);
        }
      } catch (err) {
        console.error("❌ Error loading tenants:", err);
      }
    };
    loadTenants();
  }, [roleCode, userTenantId]);

  // ============================================================
  // 🔹 Load Locations, Departments, Roles
  // ============================================================
  useEffect(() => {
    const loadData = async () => {
      if (!selectedTenant) return;
      const tenantId = selectedTenant.value;
      try {
        const [locData, depData, roleData] = await Promise.all([
          getLocationsByTenant(tenantId),
          getDepartmentsByTenant(tenantId),
          getRolesByTenant(tenantId),
        ]);
        setLocations((locData || []).map((l) => ({ value: l.id, label: l.name })));
        setDepartments((depData || []).map((d) => ({ value: d.id, label: d.name })));
        setRoles((roleData || []).map((r) => ({ value: r.id, label: r.name })));
        console.log(
          "Locations State:",
          (locData || []).map((l) => ({
            value: l.id,
            label: l.name,
          }))
        );

        console.log(
          "Departments State:",
          (depData || []).map((d) => ({
            value: d.id,
            label: d.name,
          }))
        );

        console.log(
          "Roles State:",
          (roleData || []).map((r) => ({
            value: r.id,
            label: r.name,
          }))
        );
      } catch (err) {
        console.error("❌ Error loading data:", err);
      }
    };
    loadData();
  }, [selectedTenant]);

  // ============================================================
  // 🔹 Handle Input
  // ============================================================
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  // ============================================================
  // 💾 Save User
  // ============================================================
// ============================================================
// 💾 Save User — Fixed 2025-11-04 22:25 IST
// Properly handles Flask "status": "success" response
// ============================================================
const handleSave = async () => {
  if (!formData.name || !formData.email || !selectedTenant) {
    alert("Please fill in required fields: Tenant, Name, and Email.");
    return;
  }

  const payload = {
    tenant_id: selectedTenant.value,
    name: formData.name,
    email: formData.email,
    mobile: formData.mobile,
    is_employee: formData.is_employee ? 1 : 0,
    status: formData.status,
    location_ids: selectedLocations.map((l) => l.value),
    department_ids: selectedDepartments.map((d) => d.value),
    role_ids: selectedRoles.map((r) => r.value),
  };

  try {
    setSaving(true);
    const res = await createUser(payload);

    // ✅ Support both Axios + Flask style responses
    const status =
      res?.status || res?.data?.status || "";
    const message =
      res?.message || res?.data?.message || "User created successfully.";

    if (status.toLowerCase() === "success") {
      alert(`✅ ${message}`);
      onSuccess?.();
      onClose?.();
    } else {
      alert(`❌ Failed to create user: ${message}`);
    }
  } catch (err) {
    console.error("❌ handleSave failed:", err);
    const errMsg =
      err?.response?.data?.message || err.message || "Error while saving user.";
    alert(`❌ ${errMsg}`);
  } finally {
    setSaving(false);
  }
};
  // ============================================================
  // 🧭 Drawer UI
  // ============================================================
  return (
    <div className="uf-modal" onClick={onClose}>
      <div className="uf-card" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="uf-header d-flex justify-content-between align-items-center">
          <h5 className="fw-bold mb-0">Add User</h5>
          <button className="btn btn-sm btn-light" onClick={onClose}>
            ✖
          </button>
        </div>

        {/* Form Body */}
        <div className="uf-body">
          <label className="form-label mt-2">Tenant</label>
          <Select
            options={tenants}
            value={selectedTenant}
            onChange={setSelectedTenant}
            isDisabled={roleCode !== "SUPER_ADMIN"}
            placeholder="Select Tenant..."
            //styles={selectStyles}
            //menuPortalTarget={document.body}
            //menuPosition="fixed"
          />

          <label className="form-label mt-3">Location(s)</label>
          <Select
            isMulti
            options={locations}
            value={selectedLocations}
            onChange={setSelectedLocations}
            placeholder="Select Location(s)..."
            //styles={selectStyles}
            //menuPortalTarget={document.body}
            //menuPosition="fixed"
          />

          <label className="form-label mt-3">Department(s)</label>
          <Select
            isMulti
            options={departments}
            value={selectedDepartments}
            onChange={setSelectedDepartments}
            placeholder="Select Department(s)..."
            //styles={selectStyles}
            //menuPortalTarget={document.body}
            //menuPosition="fixed"
          />

          <label className="form-label mt-3">Role(s)</label>
          <Select
            isMulti
            options={roles}
            value={selectedRoles}
            onChange={setSelectedRoles}
            placeholder="Select Role(s)..."
            //styles={selectStyles}
            //menuPortalTarget={document.body}
            //menuPosition="fixed"
          />

          <label className="form-label mt-3">Name</label>
          <input
            type="text"
            className="form-control"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="Enter full name"
          />

          <label className="form-label mt-3">Email</label>
          <input
            type="email"
            className="form-control"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="Enter email"
          />

          {/* Make Employee */}
          <div className="form-check form-switch mt-3">
            <input
              className="form-check-input"
              type="checkbox"
              id="makeEmployee"
              name="is_employee"
              checked={formData.is_employee}
              onChange={handleChange}
            />
            <label className="form-check-label" htmlFor="makeEmployee">
              Make this user an Employee?
            </label>
          </div>

          <label className="form-label mt-3">Mobile</label>
          <input
            type="text"
            className="form-control"
            name="mobile"
            value={formData.mobile}
            onChange={handleChange}
            placeholder="Enter mobile number"
          />

          {/* Status */}
          <div className="form-check form-switch mt-3">
            <input
              className="form-check-input"
              type="checkbox"
              id="statusSwitch"
              checked={formData.status === "Active"}
              onChange={() =>
                setFormData((prev) => ({
                  ...prev,
                  status: prev.status === "Active" ? "Inactive" : "Active",
                }))
              }
            />
            <label className="form-check-label" htmlFor="statusSwitch">
              Status: {formData.status}
            </label>
          </div>
        </div>

        {/* Footer Buttons */}
       {/* Footer Buttons */}



{/* ✅ Footer Buttons — Updated 2025-11-04 to match modern style */}
<div
  className="uf-actions d-flex justify-content-end align-items-center gap-3 mt-4 p-3"
  style={{ backgroundColor: "#E8F8EE", borderTop: "1px solid #d8f0e0" }}
>
  {/* Cancel */}
  <button
    type="button"
    onClick={onClose}
    className="btn"
    style={{
      backgroundColor: "#6c757d",
      color: "white",
      padding: "8px 20px",
      borderRadius: "6px",
      fontWeight: 600,
      border: "none",
    }}
  >
    Cancel
  </button>

  {/* Save */}
  <button
    onClick={handleSave}
    disabled={saving}
    className="btn"
    style={{
      background: "linear-gradient(90deg, #007bff, #6f42c1)",
      color: "white",
      border: "none",
      padding: "8px 22px",
      borderRadius: "6px",
      fontWeight: 600,
      opacity: saving ? 0.7 : 1,
    }}
  >
    {saving ? (
      <>
        <Spinner animation="border" size="sm" /> Saving...
      </>
    ) : (
      "Save Changes"
    )}
  </button>
</div>
      </div>
    </div>
  );
}
